import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { APP, ABOUT } from "@/constants/testIds";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { AppFooter } from "@/components/AppFooter";
import { RootsMark } from "@/components/RootsMark";
import { AboutDialog } from "@/components/AboutDialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Database, FolderKanban, Users, History,
  HardHat, LogOut, Building2, MapPin, FileText, RefreshCw, CheckCircle2, ChevronDown, Award, Radar, SlidersHorizontal, BadgeCheck, Info, Layers, Recycle,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: APP.navDashboard, end: true },
  { to: "/project", label: "Project Record", icon: FolderKanban, testid: APP.navProject },
  { to: "/tower", label: "Control Tower", icon: Radar, testid: "nav-tower", roles: ["tenant_admin", "contract_manager", "supervisor", "finance_reviewer"] },
  { to: "/tender", label: "Tender Packs", icon: Award, testid: APP.navTender, hideFor: ["auditor"] },
  { to: "/waste", label: "Waste & Subcontractors", icon: Recycle, testid: "nav-waste", feature: "waste_subcontracting", roles: ["tenant_admin", "contract_manager", "client_rep", "auditor"] },
  { to: "/standards", label: "Standards", icon: BadgeCheck, testid: "nav-standards", roles: ["tenant_admin", "contract_manager", "supervisor", "finance_reviewer", "client_rep", "auditor"] },
  { to: "/setup", label: "Setup & Data", icon: Database, testid: APP.navSetup, hideFor: ["auditor"] },
  { to: "/method", label: "Scoring Method", icon: SlidersHorizontal, testid: "nav-method", adminOnly: true },
  { to: "/team", label: "Team & Roles", icon: Users, testid: APP.navTeam, adminOnly: true },
  { to: "/audit", label: "Audit Trail", icon: History, testid: APP.navAudit },
];

const navVisible = (n, hasRole, tenant) =>
  (!n.feature || tenant?.features?.[n.feature]) &&
  (!n.adminOnly || hasRole("tenant_admin")) && (!n.roles || hasRole(...n.roles)) && (!n.hideFor || !hasRole(...n.hideFor));

function LestariBadge() {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/lestari/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const { hasRole } = useAuth();
  const sync = async () => {
    try {
      const { data } = await api.post("/lestari/sync");
      setStatus(data);
      toast.success("Lestari export prepared", { description: "Live API sync activates in Phase 2." });
    } catch {
      toast.error("Sync unavailable");
    }
  };
  return (
    <div className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 lg:flex" data-testid={APP.lestariStatus}>
      <span className={cn("h-2 w-2 rounded-full", status?.last_synced_at ? "bg-amber-400" : "bg-slate-400")} />
      <span className="text-xs text-emerald-50/80">
        Lestari {status?.last_synced_at ? `· synced ${fmtDateTime(status.last_synced_at)}` : "· not connected"}
      </span>
      {hasRole("tenant_admin", "contract_manager") && (
        <button onClick={sync} data-testid={APP.lestariSyncBtn} className="ml-1 text-emerald-200 hover:text-white">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function ContextBar() {
  const ws = useWorkspace();
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-white px-4 py-2 sm:px-6">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Building2 className="h-4 w-4" /> Working in
      </div>
      <Select value={ws.entityId} onValueChange={ws.setEntityId}>
        <SelectTrigger className="h-9 w-[200px] text-sm" data-testid={APP.entitySelect}>
          <SelectValue placeholder="Legal entity" />
        </SelectTrigger>
        <SelectContent>
          {ws.entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={ws.siteId} onValueChange={ws.setSiteId}>
        <SelectTrigger className="h-9 w-[200px] text-sm" data-testid={APP.siteSelect}>
          <MapPin className="mr-1 h-3.5 w-3.5 text-forest-mid" />
          <SelectValue placeholder="Site" />
        </SelectTrigger>
        <SelectContent>
          {ws.visibleSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {ws.siteId && (
        <Select value={ws.zoneId || "all"} onValueChange={ws.setZoneId}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid={APP.zoneSelect}>
            <Layers className="mr-1 h-3.5 w-3.5 text-forest-mid" />
            <SelectValue placeholder="Zone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All zones</SelectItem>
            {ws.visibleZones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Select value={ws.contractId} onValueChange={ws.setContractId}>
        <SelectTrigger className="h-9 w-[240px] text-sm" data-testid={APP.contractSelect}>
          <FileText className="mr-1 h-3.5 w-3.5 text-forest-mid" />
          <SelectValue placeholder="Contract" />
        </SelectTrigger>
        <SelectContent>
          {ws.visibleContracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, tenant, roleLabel, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas">
      {/* Top bar */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between bg-forest-deep px-4 text-emerald-50 shadow-md sm:px-6">
        <div className="flex items-center gap-2" data-testid={APP.brand}>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sage-accent">
            <RootsMark className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-heading text-lg font-bold">ROOTS</p>
            <p className="-mt-1 text-[10px] uppercase tracking-widest text-emerald-200/70">{tenant?.name}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <LestariBadge />
          {!hasRole("auditor") && (
            <Button
              variant="secondary"
              size="sm"
              className="hidden bg-sage-accent text-white hover:bg-sage-accent/90 sm:inline-flex"
              onClick={() => navigate("/field")}
              data-testid={APP.fieldModeBtn}
            >
              <HardHat className="mr-1.5 h-4 w-4" /> Field Mode
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 py-1 pl-1 pr-2.5 hover:bg-white/10" data-testid={APP.userMenu}>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sage-accent text-xs font-bold text-white">
                  {(user?.name || "?").slice(0, 1)}
                </span>
                <span className="hidden text-left leading-tight sm:block">
                  <span className="block text-xs font-semibold">{user?.name}</span>
                  <span className="block text-[10px] text-emerald-200/70" data-testid={APP.roleBadge}>{roleLabel}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!hasRole("auditor") && (
                <DropdownMenuItem onClick={() => navigate("/field")}>
                  <HardHat className="mr-2 h-4 w-4" /> Field Mode
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setAboutOpen(true)} data-testid={ABOUT.trigger}>
                <Info className="mr-2 h-4 w-4" /> About
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-60 shrink-0 border-r bg-white px-3 py-5 md:block">
          <nav className="space-y-1">
            {NAV.filter((n) => navVisible(n, hasRole, tenant)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-forest-mid text-white shadow-sm"
                      : "text-slate-600 hover:bg-sage-surface hover:text-forest-dark"
                  )
                }
              >
                <n.icon className="h-4.5 w-4.5" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 rounded-xl border border-sage-light bg-sage-surface p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-forest-dark">
              <CheckCircle2 className="h-4 w-4 text-sage-accent" /> Capture once, use many
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              Every field fact flows into metrics and the monthly evidence pack — fully traceable.
            </p>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          <ContextBar />
          <div className="px-4 py-6 sm:px-6 lg:px-8">{children}</div>
          <AppFooter />
        </main>
      </div>

      {/* Mobile nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t bg-white py-1.5 md:hidden">
        {NAV.filter((n) => navVisible(n, hasRole, tenant)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) =>
            cn("flex flex-col items-center gap-0.5 px-2 py-1 text-[10px]", isActive ? "text-forest-mid" : "text-slate-500")
          }>
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>

      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}
